import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft } from "lucide-react";
import axios from "axios";
import { useCart } from "../cart";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  full_name: "",
  phone: "",
  email: "",
  address_line: "",
  city: "",
  county: "",
  postal_code: "",
  notes: "",
};

const AUTOCOMPLETE = {
  full_name: "name",
  phone: "tel",
  email: "email",
  address_line: "street-address",
  city: "address-level2",
  county: "address-level1",
  postal_code: "postal-code",
};

const Field = ({ id, label, type = "text", value, onChange, error, full = false }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <label htmlFor={id} className="block text-[10px] uppercase tracking-[0.25em] text-[#6E665A] mb-2">
      {label} <span className="text-[#B8954C]">*</span>
    </label>
    <input
      id={id}
      name={id}
      type={type}
      value={value}
      onChange={onChange}
      autoComplete={AUTOCOMPLETE[id] || "off"}
      className={`input-light ${error ? "!border-b-red-400" : ""}`}
      data-testid={`delivery-${id}`}
    />
  </div>
);

const CheckoutForm = ({ open, onClose, onBack }) => {
  const { items, total } = useCart();
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const setField = (k) => (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: false }));
  };

  const validate = () => {
    const required = ["full_name", "phone", "email", "address_line", "city", "county", "postal_code"];
    const errs = {};
    required.forEach((k) => {
      if (!form[k] || !String(form[k]).trim()) errs[k] = true;
    });
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = true;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Câmp obligatoriu");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.id, quantity: i.qty })),
        origin_url: window.location.origin,
        delivery: { ...form },
      };
      const r = await axios.post(`${API}/checkout/session`, payload);
      if (r.data.url) {
        window.location.href = r.data.url;
      } else {
        toast.error("Checkout failed");
        setLoading(false);
      }
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.error(err);
      }
      toast.error(err?.response?.data?.detail || "Checkout failed");
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-[#1A1612]/40 backdrop-blur-sm z-[80]"
            data-testid="checkout-form-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.5, ease: [0.32, 0.72, 0, 1] }}
            className="fixed top-0 right-0 bottom-0 w-full sm:w-[560px] bg-[#FAF5EC] border-l border-[#E5DDD0] z-[90] flex flex-col"
            data-testid="checkout-form-drawer"
          >
            <header className="flex items-center justify-between p-8 border-b border-[#E5DDD0]">
              <button
                onClick={onBack}
                className="text-[#6E665A] hover:text-[#1A1612] transition flex items-center gap-2 text-sm"
                data-testid="checkout-form-back"
              >
                <ChevronLeft size={16} />
                Înapoi la coș
              </button>
              <button
                onClick={onClose}
                className="text-[#6E665A] hover:text-[#1A1612] transition"
                data-testid="checkout-form-close"
                aria-label="Close"
              >
                <X size={22} strokeWidth={1.4} />
              </button>
            </header>

            <div className="flex-1 overflow-y-auto p-8">
              <p className="text-xs uppercase tracking-[0.4em] text-[#B8954C] mb-4">
                Detalii Livrare
              </p>
              <h3 className="font-serif text-3xl text-[#1A1612] mb-4 leading-tight">
                Detalii Livrare
              </h3>
              <p className="text-sm text-[#6E665A] font-light leading-relaxed mb-10">
                Te rugăm să completezi adresa pentru livrare. După confirmare, vei fi redirecționat spre plata securizată.
              </p>

              <form id="delivery-form" onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-7">
                <Field id="full_name" label="Nume complet" value={form.full_name} onChange={setField("full_name")} error={errors.full_name} full />
                <Field id="phone" label="Telefon" type="tel" value={form.phone} onChange={setField("phone")} error={errors.phone} />
                <Field id="email" label="Email" type="email" value={form.email} onChange={setField("email")} error={errors.email} />
                <Field id="address_line" label="Adresă (stradă, nr., bloc, ap.)" value={form.address_line} onChange={setField("address_line")} error={errors.address_line} full />
                <Field id="city" label="Oraș" value={form.city} onChange={setField("city")} error={errors.city} />
                <Field id="county" label="Județ" value={form.county} onChange={setField("county")} error={errors.county} />
                <Field id="postal_code" label="Cod poștal" value={form.postal_code} onChange={setField("postal_code")} error={errors.postal_code} />
                <div className="sm:col-span-2">
                  <label htmlFor="notes" className="block text-[10px] uppercase tracking-[0.25em] text-[#6E665A] mb-2">
                    Observații (opțional)
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    rows={2}
                    value={form.notes}
                    onChange={setField("notes")}
                    className="input-light resize-none"
                    data-testid="delivery-notes"
                  />
                </div>
              </form>

              <div className="mt-12 pt-8 border-t border-[#E5DDD0]">
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#B8954C] mb-4">
                  Sumar comandă
                </p>
                <ul className="space-y-2 mb-6">
                  {items.map((it) => (
                    <li key={it.id} className="flex items-center justify-between text-sm">
                      <span className="text-[#1A1612] font-light">
                        {it.name} <span className="text-[#6E665A]">× {it.qty}</span>
                      </span>
                      <span className="text-[#1A1612]">
                        {(Number(it.price) * it.qty).toFixed(0)} RON
                      </span>
                    </li>
                  ))}
                </ul>
                <div className="flex items-baseline justify-between pt-4 border-t border-[#E5DDD0]">
                  <span className="text-xs uppercase tracking-[0.3em] text-[#6E665A]">Total</span>
                  <span className="font-serif text-2xl text-[#1A1612]" data-testid="delivery-total">
                    {total.toFixed(0)} <span className="text-[#B8954C] text-xs uppercase tracking-[0.2em]">RON</span>
                  </span>
                </div>
              </div>
            </div>

            <footer className="p-8 border-t border-[#E5DDD0]">
              <button
                type="submit"
                form="delivery-form"
                disabled={loading}
                className="btn-pill w-full"
                data-testid="checkout-form-submit"
              >
                {loading ? "..." : "Continuă spre plată →"}
              </button>
            </footer>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default CheckoutForm;
